import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, RefreshCw, Plus, Edit, Trash2, Shield, 
  User, CheckCircle, XCircle, Clock, Key,
  ShoppingCart, CreditCard, Receipt, Building2, FileText, Settings
} from 'lucide-react';

const UsuariosPage = () => {
  const { user, empresas } = useApp();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [permisosModalOpen, setPermisosModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [usuarioPermisos, setUsuarioPermisos] = useState(null);
  const [permisos, setPermisos] = useState({});
  const [modulosDisponibles, setModulosDisponibles] = useState({});
  const [savingPermisos, setSavingPermisos] = useState(false);
  
  const [form, setForm] = useState({
    username: '',
    password: '',
    nombre: '',
    email: '',
    rol: 'USUARIO',
    empresa_id: '',
    activo: true
  });

  // Cargar usuarios
  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/usuarios');
      setUsuarios(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver usuarios');
      } else {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar módulos disponibles
  const cargarModulos = useCallback(async () => {
    try {
      const res = await api.get('/permisos/modulos');
      setModulosDisponibles(res.data);
    } catch (error) {
      console.error('Error cargando módulos:', error);
    }
  }, []);

  useEffect(() => {
    cargarUsuarios();
    cargarModulos();
  }, [cargarUsuarios, cargarModulos]);

  // Abrir modal para nuevo usuario
  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      username: '',
      password: '',
      nombre: '',
      email: '',
      rol: 'USUARIO',
      empresa_id: '',
      activo: true
    });
    setModalOpen(true);
  };

  // Abrir modal para editar
  const abrirEditar = (usuario) => {
    setEditando(usuario);
    setForm({
      username: usuario.username,
      password: '',
      nombre: usuario.nombre,
      email: usuario.email || '',
      rol: usuario.rol,
      empresa_id: usuario.empresa_id ? String(usuario.empresa_id) : '',
      activo: usuario.activo
    });
    setModalOpen(true);
  };

  // Abrir modal de permisos
  const abrirPermisos = async (usuario) => {
    setUsuarioPermisos(usuario);
    try {
      const res = await api.get(`/permisos/usuario/${usuario.id}`);
      setPermisos(res.data);
    } catch (error) {
      toast.error('Error al cargar permisos');
      setPermisos({});
    }
    setPermisosModalOpen(true);
  };

  // Cambiar un permiso
  const togglePermiso = (modulo, subModulo, accion) => {
    const key = `${modulo}.${subModulo}`;
    const current = permisos[key] || {
      modulo,
      sub_modulo: subModulo,
      puede_ver: false,
      puede_crear: false,
      puede_editar: false,
      puede_eliminar: false
    };
    
    setPermisos({
      ...permisos,
      [key]: {
        ...current,
        [accion]: !current[accion]
      }
    });
  };

  // Marcar/desmarcar todos los permisos de un módulo
  const toggleModuloCompleto = (moduloKey, checked) => {
    const modulo = modulosDisponibles[moduloKey];
    if (!modulo) return;
    
    const newPermisos = { ...permisos };
    modulo.sub_modulos.forEach(sub => {
      const key = `${moduloKey}.${sub.key}`;
      newPermisos[key] = {
        modulo: moduloKey,
        sub_modulo: sub.key,
        puede_ver: checked,
        puede_crear: checked,
        puede_editar: checked,
        puede_eliminar: checked
      };
    });
    setPermisos(newPermisos);
  };

  // Marcar/desmarcar una acción para todo un módulo
  const toggleAccionModulo = (moduloKey, accion, checked) => {
    const modulo = modulosDisponibles[moduloKey];
    if (!modulo) return;
    
    const newPermisos = { ...permisos };
    modulo.sub_modulos.forEach(sub => {
      const key = `${moduloKey}.${sub.key}`;
      newPermisos[key] = {
        ...newPermisos[key] || {
          modulo: moduloKey,
          sub_modulo: sub.key,
          puede_ver: false,
          puede_crear: false,
          puede_editar: false,
          puede_eliminar: false
        },
        [accion]: checked
      };
    });
    setPermisos(newPermisos);
  };

  // Guardar permisos
  const guardarPermisos = async () => {
    setSavingPermisos(true);
    try {
      const permisosArray = Object.values(permisos).map(p => ({
        modulo: p.modulo,
        sub_modulo: p.sub_modulo,
        puede_ver: p.puede_ver || false,
        puede_crear: p.puede_crear || false,
        puede_editar: p.puede_editar || false,
        puede_eliminar: p.puede_eliminar || false
      }));
      
      await api.put(`/permisos/usuario/${usuarioPermisos.id}`, { permisos: permisosArray });
      toast.success('Permisos guardados correctamente');
      setPermisosModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar permisos');
    } finally {
      setSavingPermisos(false);
    }
  };

  // Guardar usuario
  const guardarUsuario = async () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!editando && !form.username.trim()) {
      toast.error('El username es requerido');
      return;
    }
    if (!editando && !form.password) {
      toast.error('La contraseña es requerida');
      return;
    }

    try {
      const data = {
        nombre: form.nombre,
        email: form.email || null,
        rol: form.rol,
        empresa_id: form.empresa_id ? parseInt(form.empresa_id) : null,
        activo: form.activo
      };

      if (!editando) {
        data.username = form.username.toLowerCase().trim();
        data.password = form.password;
        await api.post('/usuarios', data);
        toast.success('Usuario creado correctamente');
      } else {
        if (form.password) {
          data.password = form.password;
        }
        await api.put(`/usuarios/${editando.id}`, data);
        toast.success('Usuario actualizado');
      }
      
      setModalOpen(false);
      cargarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  // Eliminar usuario
  const eliminarUsuario = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/usuarios/${deleteConfirm.id}`);
      toast.success('Usuario eliminado');
      setDeleteConfirm(null);
      cargarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  // Badge de rol
  const getRolBadge = (rol) => {
    const styles = {
      ADMIN: 'bg-purple-100 text-purple-700',
      USUARIO: 'bg-blue-100 text-blue-700',
      LECTURA: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={styles[rol]}>{rol}</Badge>;
  };

  // Icono del módulo
  const getModuloIcon = (moduloKey) => {
    const icons = {
      compras: Receipt,
      ventas: FileText,
      bancos: CreditCard,
      planilla: Users,
      catalogos: Building2,
      administracion: Settings,
      reportes: FileText
    };
    const Icon = icons[moduloKey] || ShoppingCart;
    return <Icon className="w-5 h-5" />;
  };

  // Check if current user is admin
  const isAdmin = user?.rol === 'ADMIN';

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">Acceso Restringido</h2>
        <p className="text-gray-400 mt-2">Solo los administradores pueden gestionar usuarios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-indigo-600" />
            Gestión de Usuarios
          </h1>
          <p className="text-gray-500 text-sm">Administra los usuarios y sus permisos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cargarUsuarios} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={abrirNuevo} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Usuarios ({usuarios.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Cargando usuarios...
                    </TableCell>
                  </TableRow>
                ) : usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : usuarios.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-mono font-medium">{u.username}</TableCell>
                    <TableCell>{u.nombre}</TableCell>
                    <TableCell className="text-sm text-gray-500">{u.email || '-'}</TableCell>
                    <TableCell>{getRolBadge(u.rol)}</TableCell>
                    <TableCell>
                      {u.activo ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" /> Activo
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="w-3 h-3 mr-1" /> Inactivo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {u.ultimo_login ? (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(u.ultimo_login).toLocaleString('es-PE')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => abrirEditar(u)}
                          title="Editar usuario"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {u.rol !== 'ADMIN' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => abrirPermisos(u)}
                            title="Configurar permisos"
                            className="text-amber-600 hover:text-amber-700"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        {u.id !== user?.id && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (window.confirm(`¿Eliminar usuario ${u.nombre || u.username}?`)) {
                                try {
                                  await api.delete(`/usuarios/${u.id}`);
                                  toast.success('Usuario eliminado');
                                  cargarUsuarios();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
                                }
                              }
                            }}
                            title="Eliminar usuario"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal Crear/Editar Usuario */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
            <DialogDescription>
              {editando ? 'Modifica los datos del usuario' : 'Completa los datos del nuevo usuario'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {!editando && (
              <div>
                <Label>Username *</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({...form, username: e.target.value})}
                  placeholder="usuario"
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({...form, nombre: e.target.value})}
                placeholder="Juan Pérez"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="usuario@empresa.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>{editando ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm({...form, rol: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Administrador</SelectItem>
                  <SelectItem value="USUARIO">Usuario</SelectItem>
                  <SelectItem value="LECTURA">Solo Lectura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Empresa Asignada</Label>
              <Select value={form.empresa_id || "all"} onValueChange={(v) => setForm({...form, empresa_id: v === "all" ? "" : v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todas las empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {empresas.map(e => (
                    <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {editando && (
              <div className="flex items-center justify-between">
                <Label>Usuario Activo</Label>
                <Switch 
                  checked={form.activo} 
                  onCheckedChange={(v) => setForm({...form, activo: v})}
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={guardarUsuario} className="bg-indigo-600 hover:bg-indigo-700">
              {editando ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Permisos */}
      <Dialog open={permisosModalOpen} onOpenChange={setPermisosModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Permisos de {usuarioPermisos?.nombre}
            </DialogTitle>
            <DialogDescription>
              Configura qué puede ver, crear, editar y eliminar este usuario
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            {Object.entries(modulosDisponibles).map(([moduloKey, modulo]) => (
              <Card key={moduloKey} className="overflow-hidden">
                <CardHeader className="bg-gray-50 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getModuloIcon(moduloKey)}
                      <span className="font-semibold">{modulo.nombre}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <button 
                        onClick={() => toggleModuloCompleto(moduloKey, true)}
                        className="text-green-600 hover:underline"
                      >
                        Marcar todo
                      </button>
                      <button 
                        onClick={() => toggleModuloCompleto(moduloKey, false)}
                        className="text-red-600 hover:underline"
                      >
                        Desmarcar todo
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/50">
                        <TableHead className="w-[200px]">Sub-módulo</TableHead>
                        <TableHead className="text-center w-24">
                          <div className="flex flex-col items-center">
                            <span>Ver</span>
                            <Checkbox 
                              className="mt-1"
                              onCheckedChange={(checked) => toggleAccionModulo(moduloKey, 'puede_ver', checked)}
                            />
                          </div>
                        </TableHead>
                        <TableHead className="text-center w-24">
                          <div className="flex flex-col items-center">
                            <span>Crear</span>
                            <Checkbox 
                              className="mt-1"
                              onCheckedChange={(checked) => toggleAccionModulo(moduloKey, 'puede_crear', checked)}
                            />
                          </div>
                        </TableHead>
                        <TableHead className="text-center w-24">
                          <div className="flex flex-col items-center">
                            <span>Editar</span>
                            <Checkbox 
                              className="mt-1"
                              onCheckedChange={(checked) => toggleAccionModulo(moduloKey, 'puede_editar', checked)}
                            />
                          </div>
                        </TableHead>
                        <TableHead className="text-center w-24">
                          <div className="flex flex-col items-center">
                            <span>Eliminar</span>
                            <Checkbox 
                              className="mt-1"
                              onCheckedChange={(checked) => toggleAccionModulo(moduloKey, 'puede_eliminar', checked)}
                            />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {modulo.sub_modulos.map((sub) => {
                        const key = `${moduloKey}.${sub.key}`;
                        const p = permisos[key] || {};
                        return (
                          <TableRow key={sub.key}>
                            <TableCell className="font-medium">{sub.nombre}</TableCell>
                            <TableCell className="text-center">
                              <Checkbox 
                                checked={p.puede_ver || false}
                                onCheckedChange={() => togglePermiso(moduloKey, sub.key, 'puede_ver')}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox 
                                checked={p.puede_crear || false}
                                onCheckedChange={() => togglePermiso(moduloKey, sub.key, 'puede_crear')}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox 
                                checked={p.puede_editar || false}
                                onCheckedChange={() => togglePermiso(moduloKey, sub.key, 'puede_editar')}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox 
                                checked={p.puede_eliminar || false}
                                onCheckedChange={() => togglePermiso(moduloKey, sub.key, 'puede_eliminar')}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPermisosModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={guardarPermisos} 
              disabled={savingPermisos}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingPermisos ? 'Guardando...' : 'Guardar Permisos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el usuario <strong>{deleteConfirm?.nombre}</strong> ({deleteConfirm?.username}). 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarUsuario} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsuariosPage;
